import type { AnySceneObjectT } from "tweenly"
import { ObjectsById } from "./wrapper.helpers"

/**
 * The objects tree, flattened into the order its rows actually appear in.
 *
 * A range selection is only meaningful against what the user can SEE, so this mirrors
 * `ObjectsTreeNode`'s two rules for showing children:
 *
 * - a ticker never renders its children at all
 * - any other node renders them only while it is expanded
 *
 * Collapsed subtrees are therefore skipped entirely: shift-clicking across a folded group
 * must not quietly select rows that are not on screen.
 *
 * Pure — no DOM, no Redux. `roots` is expected already sorted (`selectSortedRootObjects`),
 * children follow `childIds`, which is the order the tree renders them in.
 */
export const flattenVisibleObjectIds = (
    roots: AnySceneObjectT[],
    objectsById: ObjectsById
): string[] => {
    const order: string[] = []

    const walk = (object: AnySceneObjectT) => {
        order.push(object.id)
        if (object.type === "ticker" || !object.isExpanded) return

        object.childIds.forEach((childId) => {
            const child = objectsById[childId]
            if (child) walk(child)
        })
    }

    roots.forEach(walk)
    return order
}

/**
 * The same objects, in the order the TIMELINE lists them — which is not the same list.
 *
 * A range has to span what the user can see, and the timeline shows a different subset of
 * the scene than the tree does (`TimelineRowWrapper`):
 *
 * - an object without animations has no row of its own; if it has children, only they are
 *   listed, and if it has neither it is left out entirely
 * - children of a ticker are dropped, together with their whole subtree (`TimelineRows`)
 * - children are always listed, whether the parent row is expanded or not — collapsing only
 *   folds away the per-ANIMATION rows, never the objects
 *
 * That last point is why this cannot reuse `isExpanded` like the tree does: in the timeline
 * the expanded flag is local component state anyway, and it does not decide which objects
 * appear.
 */
export const flattenTimelineObjectIds = (
    roots: AnySceneObjectT[],
    objectsById: ObjectsById
): string[] => {
    const order: string[] = []

    const walk = (object: AnySceneObjectT) => {
        // no animations means no row to click, so the object cannot be an end of a range
        if (Object.keys(object.animations ?? {}).length > 0) order.push(object.id)
        if (object.type === "ticker") return

        object.childIds.forEach((childId) => {
            const child = objectsById[childId]
            if (child) walk(child)
        })
    }

    roots.forEach(walk)
    return order
}
